import type { ReactNode } from 'react';
import { CdHeading, CdText } from '../../atoms/CdTypography';
import { cx } from '../../utils';
import styles from './CdHeroBanner.module.scss';

export interface CdHeroBannerProps {
  eyebrow?: string;
  title: string;
  description?: string;
  /** Primary/secondary CTAs. */
  actions?: ReactNode;
  /** Secondary content below the actions (e.g. "Not sure which permit you need?" block). */
  children?: ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  /** `public` = large marketing hero; `app` = compact welcome banner inside the shell. */
  variant?: 'public' | 'app';
  className?: string;
}

/** Light-blue hero with copy on the left and illustration on the right (stacked on mobile). */
export const CdHeroBanner = ({
  eyebrow,
  title,
  description,
  actions,
  children,
  imageSrc,
  imageAlt = '',
  variant = 'public',
  className,
}: CdHeroBannerProps) => {
  return (
    <section className={cx(styles.hero, styles[variant], className)}>
      <div className={styles.content}>
        {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
        <CdHeading level={1} size={variant === 'public' ? 'display' : 'xl'}>
          {title}
        </CdHeading>
        {description && <CdText variant="bodyLarge">{description}</CdText>}
        {actions && <div className={styles.actions}>{actions}</div>}
        {children}
      </div>
      <div className={styles.media}>
        {imageSrc ? (
          <img src={imageSrc} alt={imageAlt} decoding="async" fetchPriority="high" />
        ) : (
          <div className={styles.placeholder} aria-hidden="true" />
        )}
      </div>
    </section>
  );
};
